import React, { useRef, useState } from 'react';
import { Box, TextField, Chip, IconButton, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { t } from 'i18next';

interface PhoneNumbersFieldProps {
  phoneNumbers: string[];
  onChange: (phoneNumbers: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  error?: boolean;
  helperText?: string;
  isTransfer?: boolean;
  showHelperText?: boolean;
}

// regex for sip numbers number validation
const phoneNumberRegex =
  /^(?:\+[0-9]{7,15}|sips?:[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+(?::\d+)?(?:;[A-Za-z0-9=]+)*|tel:[0-9]+)$/;
// regex for transfer tool number validation
const transferPhoneNumberRegex = /^\+?[0-9]{2,14}$/;

const validatePhoneNumber = (phoneNumber: string, isTransfer: boolean = false): boolean => {
  const regex = isTransfer ? transferPhoneNumberRegex : phoneNumberRegex;

  if (!regex.test(phoneNumber)) {
    return false;
  }

  if (!isTransfer) {
    const numberPart = phoneNumber.startsWith('+') ? phoneNumber.substring(1) : phoneNumber;

    // Reject if all digits are the same
    if (/^(\d)\1+$/.test(numberPart)) {
      return false;
    }

    // Reject if it's just sequential numbers
    if (numberPart === '1234567890' || numberPart === '0123456789') {
      return false;
    }
  }

  return true;
};

const getPhoneNumberError = (
  phoneNumber: string,
  isTransfer: boolean = false,
): string | undefined => {
  const regex = isTransfer ? transferPhoneNumberRegex : phoneNumberRegex;

  if (!regex.test(phoneNumber)) {
    return t('campaignPage.phoneNumberValidation.invalidFormat');
  }

  if (!isTransfer) {
    const numberPart = phoneNumber.startsWith('+') ? phoneNumber.substring(1) : phoneNumber;
    if (/^(\d)\1+$/.test(numberPart)) {
      return t('campaignPage.phoneNumberValidation.repeatedDigits');
    }

    if (numberPart === '1234567890' || numberPart === '0123456789') {
      return t('campaignPage.phoneNumberValidation.sequential');
    }
  }

  return undefined;
};

export const PhoneNumbersField: React.FC<PhoneNumbersFieldProps> = ({
  phoneNumbers = [],
  onChange,
  disabled = false,
  placeholder,
  error = false,
  helperText,
  isTransfer = false,
  showHelperText = true,
}) => {
  const phoneNumberInputRef = useRef<HTMLInputElement>(null);
  const [currentInputValue, setCurrentInputValue] = useState('');
  const [inputError, setInputError] = useState<string | undefined>();

  const handleNumberAdd = (number: string) => {
    const trimmedNumber = number.trim();
    if (trimmedNumber && !phoneNumbers.includes(trimmedNumber)) {
      if (!validatePhoneNumber(trimmedNumber, isTransfer)) {
        const errorMessage = getPhoneNumberError(trimmedNumber, isTransfer);
        setInputError(errorMessage);
        return;
      }
      onChange([...phoneNumbers, trimmedNumber]);
      setCurrentInputValue('');
      setInputError(undefined);
    } else if (phoneNumbers.includes(trimmedNumber)) {
      setInputError('This phone number is already added');
    }
  };

  const handleNumberRemove = (index: number) => {
    const newNumbers = [...phoneNumbers];
    newNumbers.splice(index, 1);
    onChange(newNumbers);
  };

  const handleNumberKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (currentInputValue.trim()) {
        handleNumberAdd(currentInputValue);
        if (phoneNumberInputRef.current) {
          phoneNumberInputRef.current.value = '';
        }
      }
    }
  };

  const handleAddNumberClick = () => {
    if (phoneNumberInputRef.current?.value) {
      handleNumberAdd(phoneNumberInputRef.current.value);
      phoneNumberInputRef.current.value = '';
    }
  };

  const handleInputChange = (value: string) => {
    setCurrentInputValue(value);
    setInputError(undefined);
    if (value.trim()) {
      const error = getPhoneNumberError(value.trim(), isTransfer);
      setInputError(error);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom></Typography>
      <TextField
        fullWidth
        placeholder={placeholder || t('campaignPage.transferCallPlaceholder')}
        onKeyPress={handleNumberKeyPress}
        disabled={disabled}
        inputRef={phoneNumberInputRef}
        value={currentInputValue}
        onChange={(e) => handleInputChange(e.target.value)}
        error={error || !!inputError}
        helperText={showHelperText ? helperText || inputError : undefined}
        InputProps={{
          endAdornment: (
            <IconButton
              onClick={handleAddNumberClick}
              disabled={disabled || !!inputError || !currentInputValue.trim()}>
              <AddIcon />
            </IconButton>
          ),
        }}
      />

      {phoneNumbers.length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {phoneNumbers.map((number, index) => (
            <Chip
              key={index}
              label={number}
              onDelete={() => handleNumberRemove(index)}
              disabled={disabled}
              color={validatePhoneNumber(number, isTransfer) ? 'primary' : 'error'}
              variant="outlined"
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
